// src/lib/branchTransferEmails.js
// Session 79.7, Issue #514. Gate decision D29, approved by Colby at 79.6's close.
//
// WHY THIS FILE EXISTS RATHER THAN sendEmail() CALLS INSIDE branchTransfers.js.
//
// Exactly the reasoning materialRequestEmails.js was created for, and it is
// locked rule 14: extract shared logic when the duplication is in the
// MUTATION/SIDE-EFFECT sequence rather than in the UI shape. "Look up the
// recipients, pick a template, send it" is that sequence, and putting it inline
// would mean branchTransfers.js -- which owns stock movement and money -- also
// owning address-book lookups. Two responsibilities, two files.
//
// It also keeps the transfer module's job legible: every function in there
// moves material and writes a bell. Nothing in there sends mail.
//
// ── WHAT GETS AN EMAIL, AND WHAT DELIBERATELY DOES NOT ──
//
// D29 splits the AUDIENCE rather than the event, and it departs from the
// instruction that produced it in one place.
//
//   EMAIL  the approver, when a transfer is raised. A branch manager sits at a
//          desk, it is their branch's material being committed, and it is their
//          turn to act.
//   EMAIL  the short close. It carries a money figure and reaches people who
//          are not in the app that day.
//   BELL   everything else -- approved, denied, shipped, received.
//
// THE FULFILLER IS DELIBERATELY NOT EMAILED and this is the half that changed.
// Colby asked for email to "approvers and fufillers at the manager level". The
// research says the fulfiller half cannot work: 83% of non-desk workers have no
// regular email access, so the person cutting steel is unreachable by inbox by
// construction rather than by preference. A queue they can see beats a message
// they will not read, and the production queue already is that queue. He took
// the correction and approved it the same evening.
//
// ── THE FAILURE RULE, and it is the one that matters operationally ──
//
// NOTHING HERE MAY ABORT A TRANSFER. That is #497's lesson made structural: a
// failed photo upload used to abort a whole receive and blame the photo, losing
// a stock count somebody had already typed. Material movement must not depend
// on a mail server.
//
// THE FIRST DRAFT OF THIS FILE CLAIMED THAT AND DID NOT DO IT, and its own test
// caught it within a minute. sendEmail() genuinely never throws -- it catches
// everything and returns { ok, error } -- so the reasoning stopped there. But
// the RECIPIENT LOOKUPS also run, they hit the database, and a rejected query
// propagated straight out and would have taken the transfer down with it.
//
// The lesson generalises past this file: "the thing I call cannot throw" is not
// the same claim as "this function cannot throw", and the gap is every other
// line in the body. Both functions now catch and log rather than rethrow.
//
// LOGGED as well as swallowed, per #258: a failure nothing reports is a
// failure nobody can find. The console line is what makes "the approver never
// got the mail" diagnosable instead of a mystery.

import {
  sendEmail,
  getRequestApproverEmails,
  getEmailsForUserIds,
  emailTransferRequested,
  emailTransferClosedShort,
  emailTransferLoadArrived,
} from './notify';

// Trigger #1 -- a transfer is raised. Goes to whoever may APPROVE it at the
// SENDING branch, because they are the ones being asked to give up material.
//
// Deliberately getRequestApproverEmails() and not getBranchManagerEmails():
// they answer different questions, and the difference is documented at length
// in notify.js. The approver set is the one canApprove() renders a button for,
// so mailing anybody else means telling somebody about a decision they cannot
// make -- which is precisely the noise #500 was logged to remove.
//
// A department is not passed. A transfer is a branch-level commitment of branch
// stock, so it resolves to the branch managers, which is what
// createTransfer()'s own in-app audience already does.
//
// ── #583, Session 79.21b: TWO STAGES, TWO AUDIENCES ─────────────────────────
//
// Since #528 a transfer waits on the ASKING branch first. `stage: 'ask'` goes
// to that branch's approvers - `recipientBranchId` is the asking branch and
// `recipientDepartmentId` the requester's department, the same lookup a
// material request makes - and says their team is asking. `stage: 'supply'`
// goes to the supplying branch when it becomes their turn, and is the email
// this function always sent - it used to be sent at creation, before the
// asking branch had agreed to anything.
//
// `fromBranchId` is kept as the fallback recipient so a caller written before
// this change still reaches the supplying branch rather than nobody.
export async function notifyTransferRequested({
  fromBranchId = null, label, fromBranchName, toBranchName,
  requesterName, mode, neededBy, lines, transferId,
  stage = 'supply', recipientBranchId = null, recipientDepartmentId = null,
}) {
  try {
    const branchId = recipientBranchId ?? fromBranchId;
    const emails = (await getRequestApproverEmails(branchId, recipientDepartmentId ?? null)) || [];
    if (!emails.length) return;
    const html = emailTransferRequested(
      label, fromBranchName, toBranchName, requesterName, mode, neededBy, lines, transferId, stage,
    );
    // The subject names the DIRECTION, because the reader's branch is the one
    // being asked and a subject reading only "Transfer requested" makes them
    // open the mail to find out whether it is theirs.
    const ownShelf = fromBranchName && fromBranchName === toBranchName;
    const what = stage === 'ask'
      ? 'Transfer to approve'
      : (mode === 'make' ? 'Material to make' : 'Transfer requested');
    const direction = ownShelf ? `for ${toBranchName}'s own shelf` : `${fromBranchName} → ${toBranchName}`;
    await sendEmail(emails, `${what} — ${direction}`, html);
  } catch (err) {
    console.error('[#514] transfer-requested email failed', { transferId, fromBranchId, recipientBranchId, err });
  }
}

// Trigger #2 -- the short close. THE SAME PEOPLE AS THE BELL, BY CONSTRUCTION.
//
// ── #683, Session 79.22b.4.3 -- ONE LIST, NOT TWO LOOKUPS ────────────────────
//
// This used to look its own audience up -- every manager at both branches and
// every admin -- while the bell used the approvers. Two lookups for one
// sentence, "both branches' managers", and they disagreed: at K10 the bell went
// to 5 people and the email to 11, so the notification screen, the record of
// who was told, understated the audience by more than half.
//
// Colby's ruling (79.22b.3.2): "the branch managers and approving managers get
// both. no admin." closeTransferShort() builds that list once and hands it
// here as user ids; this only turns them into addresses. There is nothing left
// here to choose, so the two cannot drift again. Gate D11 and D18.
//
// Deduplicated by the lookup, because a person can be a branch manager and an
// approver at once, and a duplicate address is a second copy, not a reader.
export async function notifyTransferClosedShort({
  recipientIds = [], label, fromBranchName, toBranchName,
  shortValue, reason, transferId,
}) {
  try {
    const emails = [...new Set(((await getEmailsForUserIds(recipientIds)) || []).filter(Boolean))];
    if (!emails.length) return;
    const html = emailTransferClosedShort(
      label, fromBranchName, toBranchName, shortValue, reason, transferId,
    );
    await sendEmail(emails, `Closed short — ${fromBranchName} → ${toBranchName}`, html);
  } catch (err) {
    console.error('[#514] transfer-closed-short email failed', { transferId, err });
  }
}

// Trigger #3 -- A LOAD HAS ARRIVED. #517, Session 79.10, gate D68.
//
// COLBY'S RULING, replacing the gate's own D60, and it is worth carrying the
// reasoning because the recommendation was wrong in an interesting way. D60
// treated arrival notifications as a VOLUME problem -- how many times do we
// interrupt somebody -- and argued the requester should still get exactly two
// bells however many trucks there are.
//
// AN ARRIVAL IS NOT AN INTERRUPTION. IT RELEASES WORK. His case: four hundred
// frames cover three orders at 200, 150 and 50; seventy-five land; nobody is
// told; the fifty-frame order misses its date for no reason and a customer is
// upset. A page somebody has to remember to open cannot do that job.
//
// THE APPROVER, NOT THE DRIVER, and that is #514's D29 holding rather than a
// new decision: 83% of non-desk workers have no reliable email access, so
// mailing the person standing at the rack is unreliable by construction. The
// requester and the approver both get the BELL; only the approver gets mail.
//
// getRequestApproverEmails() and not getBranchManagerEmails(), for the same
// reason trigger #1 gives: they answer different questions, and the approver
// set is the one that committed the branch to this material in the first place.
//
// THE BRANCH IS THE RECEIVING ONE. The material landed on their shelf and it is
// their work it releases -- mailing the sending branch would tell Houston that
// something they despatched last week turned up, which they already knew.
export async function notifyTransferLoadArrived({
  toBranchId, departmentId = null, label, loadRef, fromBranchName, toBranchName,
  lines, outstanding, note, transferId, ended = false,
}) {
  try {
    // #649, Session 79.22b -- the asker's department, so the manager who
    // approved stage one is mailed as well as the branch manager. Absent, it
    // is the branch alone, exactly as before.
    const emails = await getRequestApproverEmails(toBranchId, departmentId ?? null);
    const to = [...new Set((emails || []).filter(Boolean))];
    if (!to.length) return;

    const html = emailTransferLoadArrived(
      label, loadRef, fromBranchName, toBranchName, lines, outstanding, note, transferId, ended,
    );

    // The subject says which of the two things happened, because an approver
    // scanning an inbox should not have to open the mail to find out whether
    // anything is still owed.
    // #599 -- and a third thing: the transfer had already ended, so this load
    // is the last of it without being everything that was asked for.
    const complete = !ended && (Number(outstanding) || 0) <= 0;
    const subject = ended
      ? `${loadRef} arrived, nothing more coming — ${fromBranchName} → ${toBranchName}`
      : complete
        ? `All arrived — ${fromBranchName} → ${toBranchName}`
        : `${loadRef} arrived — ${fromBranchName} → ${toBranchName}`;

    await sendEmail(to, subject, html);
  } catch (err) {
    // NOTHING HERE MAY ABORT AN ARRIVAL -- the file's own failure rule, and
    // #497's lesson made structural. The stock is already booked in and the
    // bells have already gone; a mail server problem is not a reason to tell
    // the person at the rack that their count did not save.
    console.error('[#517] transfer-load-arrived email failed', { transferId, loadRef, err });
  }
}
